import type { ShippingOptionContentVariantKeysByFulfilmentDay } from '@/components/order/shippingOptions/ShippingOptionCard/types';
import {
  get12HourFormat,
  getContent,
  getFulfilmentDay,
} from '@/components/order/shippingOptions/ShippingOptionCard/utils';
import { calculateDiscountedPrice } from '@/components/order/utils';
import { Logger } from '@/utils/logger';
import { DELIVERY_OPTION, DELIVERY_OPTIONS, getAdditionalTemporalInfo } from '@montugroup/circuit-shared';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Typography } from '@montugroup/design-system';
import NightlightOutlinedIcon from '@mui/icons-material/NightlightOutlined';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import type { DeliveryOptions } from './types';

const logger = new Logger('useSameDayDeliveryOption');

// TODO: CONTENT_VARIANTS to be sourced from LaunchDarkly
const CONTENT_VARIANTS: ShippingOptionContentVariantKeysByFulfilmentDay = {
  today: {
    title: 'A',
    description: 'A',
    icon: 'A',
  },
  tomorrow: {
    title: 'A',
    description: 'A',
    icon: 'A',
  },
};

const CONTENT = {
  today: {
    icons: { A: <NightlightOutlinedIcon /> },
    titles: { A: DELIVERY_OPTIONS[DELIVERY_OPTION.SameDay].displayName },
    descriptions: {
      A: ({ cutoffTime, deliveryWindow }: { cutoffTime: string; deliveryWindow: { start: string; end: string } }) => {
        return (
          <>
            <Typography>Can only deliver to a residential address.</Typography>
            <Typography>
              Order before {cutoffTime}, arrive {deliveryWindow.start}-{deliveryWindow.end} today.
            </Typography>
          </>
        );
      },
    },
  },
  tomorrow: {
    icons: { A: <NightlightOutlinedIcon /> },
    titles: { A: DELIVERY_OPTIONS[DELIVERY_OPTION.SameDay].displayNameAfterCutoff },
    descriptions: {
      A: ({ deliveryWindow }: { cutoffTime: string; deliveryWindow: { start: string; end: string } }) => {
        return (
          <>
            <Typography>Can only deliver to a residential address.</Typography>
            <Typography>
              Arrive {deliveryWindow.start}-{deliveryWindow.end} tomorrow.
            </Typography>
          </>
        );
      },
    },
  },
} as const;

const getSettings = (option: ArrayElement<DeliveryOptions['SameDay']> | undefined, localDate: string) => {
  if (!option) {
    return null;
  }

  const { cutoffTime, deliveryWindow, expectedFulfilmentDate, shippingPrice, discount } = option;

  const fulfilmentDay = getFulfilmentDay(expectedFulfilmentDate, localDate);
  if (!fulfilmentDay) {
    return null;
  }

  const cutoffTimeHourFormatted = get12HourFormat(cutoffTime);
  const deliveryWindowHourFormatted = {
    start: get12HourFormat(deliveryWindow.startTime),
    end: get12HourFormat(deliveryWindow.endTime),
  };
  const content = getContent<(typeof CONTENT)[keyof typeof CONTENT]>({
    content: CONTENT[fulfilmentDay],
    variants: CONTENT_VARIANTS[fulfilmentDay],
    descriptionArgs: {
      cutoffTime: cutoffTimeHourFormatted,
      deliveryWindow: deliveryWindowHourFormatted,
    },
  });

  const discountedPrice = calculateDiscountedPrice({
    price: shippingPrice,
    discountPercent: discount?.percentage,
  });

  const extraDetails = {
    estimatedArrivalTime: `${deliveryWindowHourFormatted.start}-${deliveryWindowHourFormatted.end}`,
    discountPercentage: discount?.percentage,
    discountedPrice,
  };

  return {
    ...option,
    ...content,
    ...extraDetails,
  };
};

export default function useSameDayDeliveryOption(props: DeliveryOptions['SameDay']) {
  // Read on every render, so the memo picks up local midnight passing on the next render rather than waiting for the
  // delivery options to be refetched
  const localDate = DateTime.now().toISODate();
  const settings = useMemo(() => getSettings(props[0], localDate), [props, localDate]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { icon, description, ...loggableSettings } = settings ?? {};

    const additionalTemporalLogs = {
      browserTime: {
        ...getAdditionalTemporalInfo(DateTime.now().toISO()),
      },
      cutoffTime: {
        ...getAdditionalTemporalInfo(settings?.cutoffTime),
      },
      validBefore: {
        ...getAdditionalTemporalInfo(settings?.validBefore),
      },
      deliveryWindowStartTime: {
        ...getAdditionalTemporalInfo(settings?.deliveryWindow.startTime),
      },
      deliveryWindowEndTime: {
        ...getAdditionalTemporalInfo(settings?.deliveryWindow.endTime),
      },
    };

    logger.info('settings', {
      ...loggableSettings,
      shouldShow: !!settings,
      additionalTemporalLogs,
      props,
    });
  }, [settings, props]);

  return settings;
}
